// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import * as BoostSubscriptionCommands from '@app/features/boost/commands/BoostSubscriptionCommands';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import type {PaddleBoostSubscriptionState} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

// The account's monthly boost subscription, read when the boost page or the buy dialog needs it; no gateway event
// carries it.
class BoostSubscription {
	subscription: PaddleBoostSubscriptionState | null = null;
	// The most boosts the subscription may hold now; 0 when it may hold none.
	maxQuantity = 0;
	// failed: the first read failed, so nothing here says how many boosts fit; a later failure keeps what was read.
	status: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';
	private generation = 0;

	constructor() {
		makeAutoObservable<this, 'generation'>(this, {generation: false}, {autoBind: true});
	}

	async load(): Promise<void> {
		const generation = this.generation;
		if (this.status !== 'ready') this.status = 'loading';
		try {
			const response = await BoostSubscriptionCommands.fetchBoostSubscription();
			runInAction(() => {
				if (generation !== this.generation) return;
				this.subscription = response.subscription;
				this.maxQuantity = response.max_quantity;
				this.status = 'ready';
			});
		} catch {
			runInAction(() => {
				if (generation === this.generation && this.status !== 'ready') this.status = 'failed';
			});
		}
	}

	set(subscription: PaddleBoostSubscriptionState): void {
		this.subscription = subscription;
	}

	reset(): void {
		this.generation++;
		this.subscription = null;
		this.maxQuantity = 0;
		this.status = 'idle';
	}
}

const boostSubscription = new BoostSubscription();
ResettableStates.register(boostSubscription, boostSubscription.reset);
AccountScopedWork.registerCancellation(() => boostSubscription.reset());

export default boostSubscription;
